import React from "react";
import { View, Linking } from "react-native";
import { Controller, useWatch } from "react-hook-form";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { StepShell } from "../components/StepShell";
import { AppTextInput } from "@/components/ui/TextInput";
import { Checkbox } from "@/components/ui/Checkbox";
import { SelectField } from "@/components/ui/SelectField";
import { FileUpload } from "@/components/ui/FileUpload";
import { usePreVettingStep4Form } from "../hooks/usePreVettingStep4Form";
import type { TranslationKeys } from "@chinese-vendor/i18n";

const CURRENCY_OPTIONS = [
  { label: "CNY ¥ Chinese Yuan", value: "CNY" },
  { label: "USD $ US Dollar", value: "USD" },
  { label: "EUR € Euro", value: "EUR" },
];

const ACCOUNT_TYPE_OPTIONS = [
  { label: "Personal", value: "personal" },
  { label: "Business / Corporate", value: "business" },
];

export default function PreVettingStep4Screen() {
  const { t } = useI18n();
  const {
    form,
    onFieldBlur,
    handleNext,
    handleBack,
    toggleChannel,
    wechatQrFile,
    wechatQrUploadState,
    wechatQrUploadError,
    handleWechatQrFile,
    handleWechatQrRemove,
  } = usePreVettingStep4Form();

  const paymentChannels = useWatch({ control: form.control, name: "payment_channels" }) ?? [];
  const hasBank = paymentChannels.includes("bank");
  const hasAlipay = paymentChannels.includes("alipay");
  const hasWechatPay = paymentChannels.includes("wechat_pay");

  const { errors } = form.formState;

  return (
    <StepShell
      step={4}
      onNext={handleNext}
      onBack={handleBack}
      nextLabel="preVetting.step4.nextLabel"
      iconName="bank"
      title="preVetting.step4.title"
      subtitle="preVetting.step4.subtitle"
    >
      <View className="pb-4 w-full items-start">
        {/* ─── Payment channel selection ────────────────────────────── */}
        <AppText className="text-sm text-dark-text-20 mb-3">
          {t("preVetting.step4.selectChannelsLabel")}
        </AppText>
        <View className="gap-3 mb-4 flex-row flex-wrap">
          <Checkbox
            checked={hasBank}
            className="rounded-md bg-stroke-20 border border-stroke-50 py-3 px-4"
            labelClassName="flex-initial"
            onChange={() => toggleChannel("bank")}
            label="preVetting.step4.bank.title"
          />
          <Checkbox
            checked={hasAlipay}
            onChange={() => toggleChannel("alipay")}
            label="preVetting.step4.alipay.title"
            className="rounded-md bg-stroke-20 border border-stroke-50 py-3 px-4"
            labelClassName="flex-initial"
          />
          <Checkbox
            checked={hasWechatPay}
            onChange={() => toggleChannel("wechat_pay")}
            label="preVetting.step4.wechatPay.title"
            className="rounded-md bg-stroke-20 border border-stroke-50 py-3 px-4"
            labelClassName="flex-initial"
          />
        </View>
        {!!errors.payment_channels?.message && (
          <AppText role="alert" className="text-xs text-error mt-1">
            {t(errors.payment_channels.message as TranslationKeys)}
          </AppText>
        )}

        {/* ─── Corporate account note ───────────────────────────────── */}
        <View className="bg-blue-10 border border-blue-15 rounded-md p-4 w-full items-start">
          <AppText className="text-xs text-blue-50 leading-5">
            {t("preVetting.step4.corporateAccountNote")}
          </AppText>
        </View>

        {/* ─── BANK section ─────────────────────────────────────────── */}
        {hasBank && (
          <View className="border border-stroke-50 px-8 py-6 rounded-lg mt-10 w-full items-start">
            <AppText
              weight="bold"
              className="text-xs text-dark-text-15 uppercase tracking-widest mb-4"
            >
              {t("preVetting.step4.bank.title")}
            </AppText>
            {!!errors.bank?.message && (
              <AppText role="alert" className="text-xs text-error mb-3">
                {t(errors.bank.message as TranslationKeys)}
              </AppText>
            )}
            <View className="tablet:flex-row tablet:gap-4 w-full">
              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="bank.bank_account_holder_name"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.bank.accountHolderName"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      required
                      error={
                        errors.bank?.bank_account_holder_name?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>

              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="bank.bank_account_number"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.bank.accountNumber"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      keyboardType="number-pad"
                      required
                      error={
                        errors.bank?.bank_account_number?.message as TranslationKeys | undefined
                      }
                    />
                  )}
                />
              </View>
            </View>
            <View className="tablet:flex-row tablet:gap-4 w-full">
              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="bank.bank_iban_code"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.bank.ibanCode"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      autoCapitalize="characters"
                      error={errors.bank?.bank_iban_code?.message as TranslationKeys | undefined}
                    />
                  )}
                />
              </View>

              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="bank.bank_swift_code"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.bank.swiftCode"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      autoCapitalize="characters"
                      error={errors.bank?.bank_swift_code?.message as TranslationKeys | undefined}
                    />
                  )}
                />
              </View>
            </View>
            <Controller
              control={form.control}
              name="bank.bank_address"
              render={({ field: { value, onChange, onBlur } }) => (
                <AppTextInput
                  label="preVetting.step4.bank.bankAddress"
                  value={value ?? ""}
                  onChangeText={onChange}
                  onBlur={() => {
                    onBlur();
                    onFieldBlur();
                  }}
                  required
                  error={errors.bank?.bank_address?.message as TranslationKeys | undefined}
                />
              )}
            />
            <Controller
              control={form.control}
              name="bank.bank_settlement_currency"
              render={({ field: { value, onChange } }) => (
                <SelectField
                  label="preVetting.step4.bank.settlementCurrency"
                  options={CURRENCY_OPTIONS}
                  value={value ?? "CNY"}
                  onChange={onChange}
                  required
                  error={
                    errors.bank?.bank_settlement_currency?.message as TranslationKeys | undefined
                  }
                />
              )}
            />
            <View className="mt-3 mb-2">
              <Controller
                control={form.control}
                name="bank.is_bank_account_under_legal_entity_name"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    checked={value ?? false}
                    onChange={onChange}
                    label="preVetting.step4.bank.isUnderLegalEntityName"
                    required
                  />
                )}
              />
            </View>
          </View>
        )}

        {/* ─── ALIPAY section ──────────────────────────────────────── */}
        {hasAlipay && (
          <View className="border border-stroke-50 px-8 py-6 rounded-lg mt-10 w-full items-start">
            <AppText
              weight="bold"
              className="text-xs text-dark-text-15 uppercase tracking-widest mb-4"
            >
              {t("preVetting.step4.alipay.title")}
            </AppText>
            {!!errors.alipay?.message && (
              <AppText role="alert" className="text-xs text-error mb-3">
                {t(errors.alipay.message as TranslationKeys)}
              </AppText>
            )}
            <View className="tablet:flex-row tablet:gap-4 w-full">
              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="alipay.alipay_merchant_id"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.alipay.merchantId"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      required
                      error={
                        errors.alipay?.alipay_merchant_id?.message as TranslationKeys | undefined
                      }
                    />
                  )}
                />
              </View>

              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="alipay.alipay_account_holder_name"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.alipay.accountHolderName"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      required
                      error={
                        errors.alipay?.alipay_account_holder_name?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>
            </View>
            <View className="tablet:flex-row tablet:gap-4 w-full">
              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="alipay.alipay_account_email"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.alipay.accountEmail"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      required
                      error={
                        errors.alipay?.alipay_account_email?.message as TranslationKeys | undefined
                      }
                    />
                  )}
                />
              </View>

              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="alipay.alipay_settlement_currency"
                  render={({ field: { value, onChange } }) => (
                    <SelectField
                      label="preVetting.step4.alipay.settlementCurrency"
                      options={CURRENCY_OPTIONS}
                      value={value ?? "CNY"}
                      onChange={onChange}
                      required
                      error={
                        errors.alipay?.alipay_settlement_currency?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>
            </View>
            <View className="mt-3 mb-2">
              <Controller
                control={form.control}
                name="alipay.is_alipay_account_under_legal_entity_name"
                render={({ field: { value, onChange } }) => (
                  <Checkbox
                    checked={value ?? false}
                    onChange={onChange}
                    label="preVetting.step4.alipay.isUnderLegalEntityName"
                    required
                  />
                )}
              />
            </View>
          </View>
        )}

        {/* ─── WECHAT PAY section ────────────────────────────────────── */}
        {hasWechatPay && (
          <View className="border border-stroke-50 px-8 py-6 rounded-lg mt-10 w-full items-start">
            <AppText
              weight="bold"
              className="text-xs text-dark-text-15 uppercase tracking-widest mb-4"
            >
              {t("preVetting.step4.wechatPay.title")}
            </AppText>
            {!!errors.wechat_pay?.message && (
              <AppText role="alert" className="text-xs text-error mb-3">
                {t(errors.wechat_pay.message as TranslationKeys)}
              </AppText>
            )}
            <View className="tablet:flex-row tablet:gap-4 w-full">
              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="wechat_pay.wechatpay_merchant_id"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.wechatPay.merchantId"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      required
                      error={
                        errors.wechat_pay?.wechatpay_merchant_id?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>

              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="wechat_pay.wechatpay_account_holder_name"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.wechatPay.accountHolderName"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      required
                      error={
                        errors.wechat_pay?.wechatpay_account_holder_name?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>
            </View>
            <View className="tablet:flex-row tablet:gap-4 w-full">
              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="wechat_pay.wechatpay_account_email"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <AppTextInput
                      label="preVetting.step4.wechatPay.accountEmail"
                      value={value ?? ""}
                      onChangeText={onChange}
                      onBlur={() => {
                        onBlur();
                        onFieldBlur();
                      }}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      required
                      error={
                        errors.wechat_pay?.wechatpay_account_email?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>

              <View className="flex-1">
                <Controller
                  control={form.control}
                  name="wechat_pay.wechatpay_settlement_currency"
                  render={({ field: { value, onChange } }) => (
                    <SelectField
                      label="preVetting.step4.wechatPay.settlementCurrency"
                      options={CURRENCY_OPTIONS}
                      value={value ?? "CNY"}
                      onChange={onChange}
                      required
                      error={
                        errors.wechat_pay?.wechatpay_settlement_currency?.message as
                          | TranslationKeys
                          | undefined
                      }
                    />
                  )}
                />
              </View>
            </View>
            <Controller
              control={form.control}
              name="wechat_pay.wechatpay_account_type"
              render={({ field: { value, onChange } }) => (
                <SelectField
                  label="preVetting.step4.wechatPay.accountType"
                  options={ACCOUNT_TYPE_OPTIONS}
                  value={value ?? ""}
                  onChange={onChange}
                  required
                  error={
                    errors.wechat_pay?.wechatpay_account_type?.message as
                      | TranslationKeys
                      | undefined
                  }
                />
              )}
            />
            <FileUpload
              label="preVetting.step4.wechatPay.qrCode"
              onFile={handleWechatQrFile}
              uploadState={wechatQrUploadState}
              currentFile={wechatQrFile}
              onViewFile={wechatQrFile ? () => Linking.openURL(wechatQrFile.uri) : undefined}
              onRemoveFile={handleWechatQrRemove}
              error={
                wechatQrUploadError ??
                (errors.wechat_pay?.wechatpay_qr_string?.message as TranslationKeys | undefined)
              }
            />
          </View>
        )}
      </View>
    </StepShell>
  );
}
